import { useMemo } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import { EarnFormValues } from '~/components/forms/earn/earnSchema'
import { SupportedChainId } from '~/constants/chains'
import { useActiveOrg } from '~/hooks/org'
import { useAddCurrentFormToBatch } from '~/hooks/useAddCurrentFormToBatch'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { useCanAddToBatch } from '~/hooks/useTransactionBatch'
import { BasicTransactionType, EarnAction, OrgSmartAccount } from '~/types'
import { Address } from '~/utils/address'
import { buildEarnBatchMetadata } from '~/utils/smartAccounts/batch'
import { bpsToPercentage, getDefaultSlippageBps } from '~/utils/swap'
import { getAutoEarnModule } from '~/utils/vault'

export const useAddEarnToBatch = ({
  senderAccount,
  title,
  action,
  chainId,
  setIsOpen,
  onSuccess,
}: {
  senderAccount: OrgSmartAccount | undefined
  title: string
  action: EarnAction
  chainId: SupportedChainId
  setIsOpen: (open: boolean) => void
  onSuccess?: () => void
}) => {
  const methods = useFormContext<EarnFormValues>()
  const { featureFlags } = useActiveOrg()
  const { addCurrentFormToBatch, isProcessing } = useAddCurrentFormToBatch({
    onSuccess,
  })

  const { control } = methods
  const calls = useWatch({ control, name: 'calls' })
  const assetToken = useWatch({ control, name: 'asset.token' })
  const assets = useWatch({ control, name: 'asset.amount' })
  const vaultToken = useWatch({ control, name: 'vault.token' })
  const shares = useWatch({ control, name: 'vault.amount' })
  const sellToken = useWatch({ control, name: 'swapData.sellToken.token' })
  const sellAmount = useWatch({ control, name: 'swapData.sellToken.amount' })
  const swapRouter = useWatch({ control, name: 'swapData.swapRouter' })

  const assetTokenMetadata = useTokenMetadata({ address: assetToken, chainId })
  const vaultTokenMetadata = useTokenMetadata({ address: vaultToken, chainId })
  const sellTokenMetadata = useTokenMetadata({ address: sellToken, chainId })

  const transactionTypes = useMemo(() => {
    if (action === EarnAction.DEPOSIT && sellToken !== assetToken) {
      return [BasicTransactionType.Earn, BasicTransactionType.Swap]
    }
    return [BasicTransactionType.Earn]
  }, [action, sellToken, assetToken])

  const canAddToBatch = useCanAddToBatch({
    smartAccountId: senderAccount?.id,
    chainId,
  })

  // Defaulted 18 would silently misscale 6-decimal tokens like USDC; gate the
  // Add-to-batch button on token metadata being resolved.
  const isMetadataReady =
    !!assetTokenMetadata?.validToken &&
    !!vaultTokenMetadata?.validToken &&
    (!sellToken ||
      Address.equal(sellToken, assetToken) ||
      !!sellTokenMetadata?.validToken)

  const handleAddToBatch = async () => {
    if (
      !senderAccount ||
      !calls?.length ||
      !isMetadataReady ||
      !assets ||
      !shares
    ) {
      return
    }
    await addCurrentFormToBatch({
      smartAccountId: senderAccount.id,
      chainId,
      title,
      types: transactionTypes,
      calls,
      metadata: buildEarnBatchMetadata({
        action,
        chainId,
        assetToken: assetToken as Address.Type,
        assetAmount: assets,
        assetTokenDecimals: assetTokenMetadata.decimals,
        vaultToken: vaultToken as Address.Type,
        vaultAmount: shares,
        vaultTokenDecimals: vaultTokenMetadata.decimals,
        sellToken: sellToken as Address.Type | undefined,
        sellAmount,
        sellTokenDecimals: sellTokenMetadata?.decimals,
        swapRouter,
        // Earn form has no user-controlled slippage — match the non-batch path
        // (useCreateEarnTransaction) and emit the symbol-based default.
        swapSlippage:
          sellToken && sellTokenMetadata?.symbol && assetTokenMetadata?.symbol
            ? bpsToPercentage(
                getDefaultSlippageBps(
                  sellTokenMetadata.symbol,
                  assetTokenMetadata.symbol,
                ),
              ).toString()
            : undefined,
      }),
      memo: methods.getValues('memo'),
      setIsOpen,
    })
  }

  return {
    handleAddToBatch,
    isProcessing,
    isMetadataReady,
    transactionTypes,
    // A batch can't carry the auto-earn pause.
    canAddToBatch:
      action === EarnAction.WITHDRAW &&
      featureFlags?.['workspace-auto-earn'] &&
      senderAccount &&
      getAutoEarnModule(senderAccount, chainId)
        ? {
            canAdd: false,
            disabledReason: 'Submit separately to pause auto-earn',
          }
        : canAddToBatch,
  }
}
